import React from 'react';
import ReactDOMServer from 'react-dom/server';

import Highcharts from 'highcharts';

import { sortingOrder, sortingTypes } from '../constants/common';

export const isDataEmpty = (data) => data.every((d) => d.series.length === 0);

export const toggleButtonActiveStyle = (svgElements, isActive, btnText) => {
  const button = svgElements?.find((el) => {
    return el?.textStr?.includes(btnText);
  });

  if (button) {
    button.attr({
      text: ReactDOMServer.renderToStaticMarkup(
        isActive ? <div style={{ fontWeight: 'bolder' }}>{btnText}</div> : <div>{btnText}</div>
      ),
    });
  }
};

export const toggleShowAllButton = (chart) => {
  const cartVar = chart;

  cartVar.showAll = !cartVar.showAll;
  toggleButtonActiveStyle(cartVar.exportSVGElements, cartVar.showAll, 'show all');
};

export const toggleLabelsButton = (chart) => {
  const cartVar = chart;

  cartVar.labels = !cartVar.labels;
  toggleButtonActiveStyle(cartVar.exportSVGElements, cartVar.labels, 'labels');
};

export const doTitle = (name, isFaded) =>
  ReactDOMServer.renderToStaticMarkup(
    isFaded ? <div>{name}</div> : <div style={{ fontWeight: 'bolder' }}>{name}</div>
  );

export const toMenuItemTitle = (name, disabled) => (disabled ? name : `[${name}]`);

export const toggleMenuItemTitle = (e, self, property, name) => {
  const { target } = e;

  target.innerText = toMenuItemTitle(name, self.userOptions[property]);
};

export const updateChartLabelsButton = (chart, fromDashboard, triggerLabels) => {
  if (!fromDashboard) {
    return;
  }

  if (chart.fullscreen.isOpen && chart.options.exporting.buttons.labels !== undefined) {
    const options = { ...chart.options };
    delete options.exporting.buttons.labels;
    chart.update(options);
  }

  if (!chart.fullscreen.isOpen && chart.options.exporting.buttons.labels === undefined) {
    const options = { ...chart.options };
    options.exporting.buttons.labels = {
      verticalAlign: 'bottom',
      align: 'left',
      text: doTitle('labels', true),
      onclick() {
        if (!this.labels) {
          this.zoomOut();
        }
        toggleLabelsButton(this);
        triggerLabels();
      },
    };

    options.exporting.buttons.labels.text = ReactDOMServer.renderToStaticMarkup(
      !chart.labels ? <div>labels</div> : <div style={{ fontWeight: 'bolder' }}>labels</div>
    );

    chart.update(options);
  }
};

export const getNameOrLegendName = (s, index, steps) => {
  const currName = s.name;

  const replaceName = (step) => {
    if (step) {
      const { legendName } = step;
      if (currName.includes('optional') && legendName) {
        return `${legendName} (optional)`;
      }
      if (legendName) {
        return legendName;
      }
      return currName;
    }
    return currName;
  };

  return replaceName(steps[s.legendIndex]);
};

export const getSeriesWithLegendIndex = (series) => {
  let legendIndex = 0;

  return series.map((item, index) => {
    if (item.name.includes('optional') && item.name.includes(series[index - 1]?.name)) {
      return {
        ...item,
        legendIndex,
      };
    }

    legendIndex += 1;

    return {
      ...item,
      legendIndex,
    };
  });
};

export const sortSeriesByType = (series, sortOrder, sortType) => {
  const copiedSeries = [...series];

  const sort = (a, b) => {
    const itemA = a[sortType];
    const itemB = b[sortType];

    const type = itemA === itemB && sortType !== sortingTypes.NAME ? sortingTypes.NAME : sortType;

    const sortA = a[type];
    const sortB = b[type];

    if (typeof sortA === 'string' && typeof sortB === 'string') {
      return sortA.localeCompare(sortB, undefined, {
        numeric: true,
        sensitivity: 'base',
      });
    }
    return sortA - sortB;
  };

  return copiedSeries.sort((a, b) => {
    return sortOrder === sortingOrder.ASCENDING ? sort(a, b) : -sort(a, b);
  });
};

export const updateSeriesWithSortedLegend = (series, chartSeries, eventSort) => {
  const sortedSeries = sortSeriesByType(chartSeries, eventSort.sortOrder, eventSort.sortType);

  series.forEach((s, i) => {
    const index = sortedSeries.findIndex((sortedItem) => sortedItem.name === s.name);
    series[i].update(
      {
        legendIndex: index,
      },
      false
    );
  });
};

export const getSortedSeries = (series, sortOrder, sortType) => {
  const sortedSeries = sortSeriesByType(series, sortOrder, sortType);

  let colorIndex = 0;
  let markerIndex = 0;

  // после сортировки заново указываем цвет и маркер для легенды
  return sortedSeries.map((s) => {
    const item = {
      ...s,
      color: Highcharts.defaultOptions.colors[colorIndex],
      legendSymbol: Highcharts.defaultOptions.symbols[markerIndex],
    };

    if (colorIndex >= 9) {
      colorIndex = 0;
    } else {
      colorIndex += 1;
    }

    if (markerIndex >= 4) {
      markerIndex = 0;
    } else {
      markerIndex += 1;
    }

    return item;
  });
};

export const toggleButtonText = (svgElements, sortBy, compareVal, text1, text2) => {
  const button = svgElements.find((el) => {
    return el?.textStr?.includes(text1) || el?.textStr?.includes(text2);
  });

  if (button) {
    button.attr({
      text: sortBy === compareVal ? text1 : text2,
    });
  }
};

export const updateUserOptionsEventSortOrder = (self) => {
  return {
    ...self.userOptions.eventSort,
    sortOrder:
      self.userOptions.eventSort.sortOrder === sortingOrder.ASCENDING
        ? sortingOrder.DESCENDING
        : sortingOrder.ASCENDING,
  };
};

export const updateUserOptionsEventSortType = (self) => {
  return {
    ...self.userOptions.eventSort,
    sortType:
      self.userOptions.eventSort.sortType === sortingTypes.NAME
        ? sortingTypes.SUM
        : sortingTypes.NAME,
  };
};

export const cycleAreaSortType = (self) => {
  const typeOrder = [sortingTypes.NAME, sortingTypes.SUM, sortingTypes.VOLATILITY];
  const currentIndex = typeOrder.indexOf(self.userOptions.eventSort.sortType);
  return {
    ...self.userOptions.eventSort,
    sortType: typeOrder[(currentIndex + 1) % typeOrder.length],
  };
};

export const swapSeriesOrderAndLegend = (chart, chartSeries, eventSort) => {
  const sortedSeries = sortSeriesByType(chartSeries, eventSort.sortOrder, eventSort.sortType);

  const isVisible = {};
  chart.series.forEach((s) => {
    isVisible[s.name] = s.visible;
  });

  while (chart.series.length > 0) {
    chart.series[0].remove(false);
  }

  sortedSeries.forEach((target, i) => {
    chart.addSeries(
      {
        ...target,
        legendIndex: i,
        visible: isVisible[target.name] !== false,
      },
      false
    );
  });
};

export const setCycleButtonText = (svgElements, currentValue, options) => {
  const button = svgElements.find((el) => options.some(({ text }) => el?.textStr?.includes(text)));
  if (button) {
    const option = options.find(({ value }) => value === currentValue);
    if (option) button.attr({ text: option.text });
  }
};

export const updateYAxis = (chart) => {
  // обновляем yAxis чтобы сразу отображалась с актуальной шкалой и не менялась при series.update() во время сортировки легенды чарта
  const { userOptions } = chart;
  if (!userOptions.yAxis?.wasUpdated && chart.yAxis[0].tickPositions?.length) {
    userOptions.yAxis.wasUpdated = true;
    chart.yAxis[0].update();
  }
};
